
import { useEventMutations } from "@/hooks/use-event-mutations";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useEventForm } from "@/hooks/use-event-form";
import { EventFormFields } from "./form/EventFormFields";
import { ValidationErrorsAlert } from "./form/ValidationErrorsAlert";
import { CreateEventButton } from "./form/CreateEventButton";
import { useValidateEventForm } from "./form/hooks/useFormValidation";
import type { ValidationErrors } from "./form/hooks/useFormValidation";
import { sentryTrace } from "@/components/performance/withPerformanceTracking";
import * as Sentry from "@sentry/react";
import { trackEvent } from "@/integrations/posthog/client";
import { useAnalytics } from "@/hooks/use-analytics";

export const EventFormComponent = () => {
  const navigate = useNavigate();
  const { trackEvent: trackAnalyticsEvent } = useAnalytics();
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const {
    title,
    setTitle,
    description,
    setDescription,
    date,
    setDate,
    preferredDates,
    setPreferredDates,
    preferredTimeOfDay,
    setPreferredTimeOfDay,
    eventLocation,
    setEventLocation,
    locationType,
    setLocationType,
    preferredVenues,
    setPreferredVenues,
    locationNote,
    setLocationNote,
    titleError,
    setTitleError,
    imageUrl,
    setImageUrl,
    imageUrls,
    setImageUrls,
    guestList,
    setGuestList,
    eventDeadline,
    setEventDeadline
  } = useEventForm();
  
  useEffect(() => {
    // Track page view when component mounts
    trackAnalyticsEvent('event_form_viewed', {
      page: 'create_event'
    });
  }, [trackAnalyticsEvent]);
  
  const {
    createDraftEvent
  } = useEventMutations(eventId => {
    // Track successful event creation
    trackEvent('event_created', {
      event_id: eventId,
      has_deadline: !!eventDeadline,
      guest_count: guestList?.length || 0
    });
    
    navigate(`/events/${eventId}`);
  });
  
  const {
    validateForm,
    isSubmitDisabled
  } = useValidateEventForm(title, preferredDates, preferredTimeOfDay, locationType, preferredVenues, locationNote);
  
  const handleCreateEvent = async () => {
    const transaction = Sentry.startTransaction({
      name: 'create-event-submit',
      data: { 
        title,
        hasDescription: !!description,
        dateCount: preferredDates?.length || 0,
        venueCount: preferredVenues?.length || 0,
        guestCount: guestList?.length || 0
      }
    });
    
    try {
      Sentry.configureScope(scope => scope.setSpan(transaction));
      
      // Track form submission attempt
      trackEvent('event_form_submitted', {
        has_title: !!title,
        has_dates: preferredDates?.length > 0,
        has_time: preferredTimeOfDay?.length > 0,
        has_location: locationType === 'specific' ? preferredVenues?.length > 0 : !!locationNote
      });
      
      const {
        errors,
        isValid
      } = validateForm();
      setValidationErrors(errors);
      if (!isValid) {
        // Track validation errors
        trackEvent('event_form_validation_error', {
          error_fields: Object.keys(errors)
        });
        
        transaction.setStatus('invalid_input');
        transaction.finish();
        return;
      }
      
      await createDraftEvent.mutateAsync({
        eventData: {
          title,
          description,
          date,
          preferredDates,
          location: locationType === 'specific' ? undefined : undefined,
          flexible_location_description: locationNote,
          imageUrl,
          status: 'planned',
          preferredTimeOfDay,
          locationType,
          preferredVenues,
          additionalImages: imageUrls,
          event_deadline: eventDeadline
        },
        selectedGuests: [],
        initialGuests: guestList
      });
      
      transaction.setStatus('ok');
    } catch (error) {
      // Track error
      trackEvent('event_creation_error', {
        error: error instanceof Error ? error.message : String(error)
      });
      
      transaction.setStatus('internal_error');
      throw error;
    } finally {
      transaction.finish();
    }
  };
  
  return <div className="min-h-screen bg-[#F5F5F5]">
      <div className="px-4 pt-6 pb-36"> {/* Added pb-36 to create more padding at the bottom */}
        <ValidationErrorsAlert errors={validationErrors} />
        <EventFormFields 
          title={title} 
          setTitle={setTitle} 
          description={description} 
          setDescription={setDescription} 
          date={date} 
          setDate={setDate} 
          preferredDates={preferredDates} 
          setPreferredDates={setPreferredDates} 
          preferredTimeOfDay={preferredTimeOfDay} 
          setPreferredTimeOfDay={setPreferredTimeOfDay} 
          eventLocation={eventLocation} 
          setEventLocation={setEventLocation} 
          locationType={locationType} 
          setLocationType={setLocationType} 
          titleError={validationErrors.title || titleError} 
          setTitleError={setTitleError} 
          imageUrl={imageUrl} 
          setImageUrl={setImageUrl} 
          imageUrls={imageUrls} 
          setImageUrls={setImageUrls} 
          preferredVenues={preferredVenues} 
          setPreferredVenues={setPreferredVenues} 
          locationNote={locationNote} 
          setLocationNote={setLocationNote} 
          guestList={guestList} 
          setGuestList={setGuestList} 
          validationErrors={validationErrors} 
          eventDeadline={eventDeadline} 
          setEventDeadline={setEventDeadline} 
        />
      </div>
      <CreateEventButton 
        onClick={handleCreateEvent} 
        disabled={isSubmitDisabled || createDraftEvent.isPending} 
        isPending={createDraftEvent.isPending} 
      />
    </div>;
};

// Wrap the component with Sentry performance tracking
// Remove 'trackRenders' which is causing the TypeScript error
export const EventForm = sentryTrace(EventFormComponent, {
  name: "EventForm",
  includeRender: true // Using the proper property instead of trackRenders
});
